import { useState } from 'react';

/**
 * The words, on a track's page and in its panel.
 *
 * ## Rendered exactly as they were typed
 *
 * `whitespace-pre-wrap`, and nothing clever. Lyrics are the one kind of text on
 * this site where the line breaks are the work rather than the formatting -
 * collapsing them into paragraphs, or trimming the blank line between verses,
 * changes what somebody wrote. The editor says the breaks are kept, and this is
 * the half of that promise that has to be true.
 *
 * ## Folded past a point, not truncated
 *
 * A long song would otherwise push the comments, the credits and everything
 * else below several screens of text - on a page whose job is a track rather
 * than a lyric sheet. So it is clipped with a gradient and a control that says
 * how to see the rest, and the whole thing is in the DOM the entire time: a
 * screen reader reads all of it, and so does a browser's own find-in-page,
 * which is exactly how somebody looks for the line they half remember.
 *
 * The threshold is line count rather than characters, because that is what
 * decides the height, and a chorus of eight short lines is not long.
 */
const FOLD_AFTER = 14;

export function Lyrics({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  const lines = text.split('\n').length;
  const long = lines > FOLD_AFTER;
  const folded = long && !open;

  return (
    <section className="flex flex-col gap-2" data-track-lyrics>
      <h2 className="meta-caps text-faint">Lyrics</h2>

      <div className="relative">
        <p
          className={`whitespace-pre-wrap text-pretty text-[15px] leading-relaxed text-text-2 ${
            folded ? 'max-h-[22rem] overflow-hidden' : ''
          }`}
        >
          {text}
        </p>

        {/*
          The fade sits over the last couple of lines rather than under a hard
          edge, so it reads as "there is more" instead of as text that stops
          mid-word for no reason. `pointer-events-none` because it covers real
          text that can still be selected.
        */}
        {folded && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
            style={{ backgroundImage: 'linear-gradient(to top, var(--color-app), transparent)' }}
          />
        )}
      </div>

      {long && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-lyrics-toggle
          className="self-start text-[13px] font-medium text-accent hover:underline"
        >
          {open ? 'Show less' : `Show all ${lines} lines`}
        </button>
      )}
    </section>
  );
}
